import { CarrierUserMenu } from "@/App/CarrierUserMenu";
import { CarrierRouter } from "@/App/CarrierRouter";
import {
  CARRIER_SECTIONS,
  parseCarrierRoute,
  sectionForRoute,
} from "@/App/carrierRoutes";
import { useCarrierRouting } from "@/App/useCarrierRouting";
import { useRecordPageView } from "@/App/useRecordPageView";
import { CarrierIdentityProvider } from "@/App/Auth/useCarrierIdentity";

// The authenticated shell: a Hubble-style top nav with the carrier-facing
// sections, plus the signed-in person's user menu. Nav items are real anchors,
// so a carrier can cmd-click a section into a new tab; the routing hook turns
// plain clicks into client-side navigations.
export function CarrierShell({
  carrierIdentifier,
  carrierName,
  userName,
  userEmail,
  onSignOut,
}: {
  carrierIdentifier: string;
  carrierName: string;
  userName: string;
  userEmail: string;
  onSignOut: () => void;
}) {
  const path = useCarrierRouting();
  const route = parseCarrierRoute(path);
  const activeSection = sectionForRoute(route);
  useRecordPageView(activeSection);

  return (
    <div className="flex min-h-screen flex-col bg-light-background">
      {/* `dark` scopes the theme's dark-surface text roles (TextColorRubric) to
        the navy bar, so text uses solid semantic colors — never white opacity. */}
      <nav className="dark flex h-11 shrink-0 items-center justify-between bg-nav-bg px-6">
        <div className="flex items-center gap-4">
          <img
            src="/logo.png"
            alt="FreightSimple"
            className="h-6 w-6 object-contain"
          />
          <span className="text-sm font-semibold text-foreground">
            Carrier Portal
          </span>
          <div className="ml-2 flex items-center gap-1">
            {CARRIER_SECTIONS.map((section) => (
              <a
                key={section.id}
                href={section.path}
                className={
                  activeSection === section.id
                    ? "rounded-md bg-white/20 px-3 py-1 text-sm font-medium text-foreground"
                    : "rounded-md px-3 py-1 text-sm font-medium text-content-secondary transition-colors hover:bg-white/10 hover:text-foreground"
                }
              >
                {section.label}
              </a>
            ))}
          </div>
        </div>
        <CarrierUserMenu
          userName={userName}
          userEmail={userEmail}
          carrierIdentifier={carrierIdentifier}
          carrierName={carrierName}
          onSignOut={onSignOut}
        />
      </nav>
      <main className="flex-1 overflow-y-auto">
        {/* Every page below here is about this one carrier, and several say so
            in their headings and stat labels. */}
        <CarrierIdentityProvider value={{ carrierIdentifier, carrierName }}>
          <CarrierRouter route={route} />
        </CarrierIdentityProvider>
      </main>
    </div>
  );
}
